import React from 'react';
import { useBrand } from './BrandProvider';
import { useTailwindBrand } from '.';

interface BrandContentProps {
  type: 'tagline' | 'description' | 'homeTitle' | 'homeSubtitle' | 'registerTitle' | 'loginTitle';
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
  children?: React.ReactNode;
  fallback?: string;
}

/**
 * Composant qui affiche du contenu dynamique selon la marque détectée
 */
export const BrandContent: React.FC<BrandContentProps> = ({
  type,
  className = '',
  tag: Tag = 'span',
  children,
  fallback
}) => {
  const brand = useBrand();
  const {classes} = useTailwindBrand();
  
  // Récupération du contenu selon le type demandé
  const getContent = (): string => {
    const content = brand.content[type];
    return content || fallback || `[${type} non défini pour ${brand.displayName}]`;
  };
  
  const content = getContent();
  
  // Si des enfants sont fournis, on peut les utiliser pour personnaliser l'affichage
  if (children) {
    return (
      <Tag className={`${className} ${classes.fontFamily}`}>
        {
        // typeof children === 'function' ? children(content, brand) : 
        children}
      </Tag>
    );
  }
  
  return (
    <Tag className={`${className} ${classes.fontFamily}`}>
      {content}
    </Tag>
  );
};

/**
 * Composant pour le tagline de la marque
 */
export const BrandTagline: React.FC<Omit<BrandContentProps, 'type'>> = (props) => (
  <BrandContent type="tagline" {...props} />
);

/**
 * Composant pour la description de la marque
 */
export const BrandDescription: React.FC<Omit<BrandContentProps, 'type'>> = (props) => (
  <BrandContent type="description" {...props} />
);

/**
 * Composant pour le titre de la page d'accueil
 */
export const BrandHomeTitle: React.FC<Omit<BrandContentProps, 'type'>> = (props) => (
  <BrandContent type="homeTitle" {...props} />
);

/**
 * Composant pour le sous-titre de la page d'accueil
 */
export const BrandHomeSubtitle: React.FC<Omit<BrandContentProps, 'type'>> = (props) => (
  <BrandContent type="homeSubtitle" {...props} />
);

/**
 * Composant pour le titre de la page d'inscription
 */
export const BrandRegisterTitle: React.FC<Omit<BrandContentProps, 'type'>> = (props) => (
  <BrandContent type="registerTitle" {...props} />
);

/**
 * Composant pour le titre de la page de connexion
 */
export const BrandLoginTitle: React.FC<Omit<BrandContentProps, 'type'>> = (props) => (
  <BrandContent type="loginTitle" {...props} />
);

/**
 * Composant pour afficher le nom de la marque
 */
export const BrandName: React.FC<{
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
  variant?: 'display' | 'short';
}> = ({ className = '', tag: Tag = 'span', variant = 'display' }) => {
  const brand = useBrand();
  
  const content = variant === 'short' ? brand.name : brand.displayName;
  
  return (
    <Tag className={className}>
      {content}
    </Tag>
  );
};

/**
 * Composant pour afficher le domaine de la marque
 */
export const BrandDomain: React.FC<{
  className?: string;
  tag?: keyof JSX.IntrinsicElements;
  includeProtocol?: boolean;
}> = ({ className = '', tag: Tag = 'span', includeProtocol = false }) => {
  const brand = useBrand();
  
  const content = includeProtocol ? `https://${brand.domain}` : brand.domain;
  
  return (
    <Tag className={className}>
      {content}
    </Tag>
  );
};

export default BrandContent;




